import { Modal } from "@mantine/core"
import React, { useContext, useEffect, useState } from "react"
import { Trans, useTranslation } from "react-i18next"
import KopyLogo from "../../assets/kopy-logo-square.png"
import {
	useAddItemToSubscription,
	useGetPriceWithTax,
	useGetProratedPriceWithTax,
} from "../../hooks/usePaddleHooks.js"
import { CoolButton } from "../CoolButton.jsx"
import { Spinner } from "../Spinner.jsx"
import { GlobalStateContext } from "../providers/GlobalStateProvider.jsx"
import { Button } from "@shopify/polaris"

export const UpgradeBasicModal = ({ open, setOpen }) => {
	const { state } = useContext(GlobalStateContext)
	const { t } = useTranslation()
	const addItemToSubscription = useAddItemToSubscription()
	const getProratedPriceWithTax = useGetProratedPriceWithTax()
	const getPriceWithTax = useGetPriceWithTax()

	const [loading, setLoading] = useState(false)
	const [error, setError] = useState(null)
	const [showSuccess, setShowSuccess] = useState(false)

	const [loadingPriceWithTax, setLoadingPriceWithTax] = useState(false)
	const [priceWithTax, setPriceWithTax] = useState(null)

	const [loadingProratedPriceWithTax, setLoadingProratedPriceWithTax] =
		useState(false)
	const [proratedPriceWithTax, setProratedPriceWithTax] = useState(null)

	const activeSubscription = state.kopyProtectSubscription || null
	const billingInterval =
		activeSubscription?.billingInterval === "monthly"
			? "monthly"
			: activeSubscription?.billingInterval === "yearly"
				? "yearly"
				: activeSubscription?.billingInterval === "quarterly"
					? "quarterly"
					: null
	const hasOtherKopyBasicStores =
		state.otherKopyBasicSubscriptions?.length > 0 || false

	const applicableKopyBasicPriceId =
		activeSubscription?.billingInterval === "monthly"
			? state.pricing?.["kopy_basic"]?.["monthly"]?.[
					hasOtherKopyBasicStores ? "discounted" : "base"
				]?.[state.hasTrialedKopyBasicBefore ? "no_trial" : "with_trial"]
					?.priceId
			: activeSubscription?.billingInterval === "yearly"
				? state.pricing?.["kopy_basic"]?.["yearly"]?.[
						hasOtherKopyBasicStores ? "discounted" : "base"
					]?.[
						state.hasTrialedKopyBasicBefore
							? "no_trial"
							: "with_trial"
					]?.priceId
				: activeSubscription?.billingInterval === "quarterly"
					? state.pricing?.["kopy_basic"]?.["quarterly"]?.[
							hasOtherKopyBasicStores ? "discounted" : "base"
						]?.[
							state.hasTrialedKopyBasicBefore
								? "no_trial"
								: "with_trial"
						]?.priceId
					: null
	const applicableKopyBasicPriceInUsd =
		activeSubscription?.billingInterval === "monthly"
			? state.pricing?.["kopy_basic"]?.["monthly"]?.[
					hasOtherKopyBasicStores ? "discounted" : "base"
				]?.[state.hasTrialedKopyBasicBefore ? "no_trial" : "with_trial"]
					?.priceInUsd
			: activeSubscription?.billingInterval === "yearly"
				? state.pricing?.["kopy_basic"]?.["yearly"]?.[
						hasOtherKopyBasicStores ? "discounted" : "base"
					]?.[
						state.hasTrialedKopyBasicBefore
							? "no_trial"
							: "with_trial"
					]?.priceInUsd
				: activeSubscription?.billingInterval === "quarterly"
					? state.pricing?.["kopy_basic"]?.["quarterly"]?.[
							hasOtherKopyBasicStores ? "discounted" : "base"
						]?.[
							state.hasTrialedKopyBasicBefore
								? "no_trial"
								: "with_trial"
						]?.priceInUsd
					: null

	const _updateSubscription = async () => {
		setError(null)

		if (!applicableKopyBasicPriceId) {
			setError("Kopy Basic Price Id not found")
			return
		}

		setLoading(true)

		const { success, errorCode } = await addItemToSubscription({
			item: "basic",
			priceId: applicableKopyBasicPriceId,
		})
		setLoading(false)

		if (success) {
			setShowSuccess(true)
			setTimeout(() => {
				setLoading(true)
			}, 1000 * 2)
			setTimeout(() => {
				window.location.reload()
			}, 1000 * 10)
		} else {
			setError(
				`Failed to upgrade subscription${errorCode === "subscription_payment_declined" ? ": Payment Declined" : ""}`,
			)
		}
	}

	useEffect(() => {
		const activeSubscriptionId = activeSubscription?.subscriptionId || null

		if (applicableKopyBasicPriceId && activeSubscriptionId) {
			setLoadingProratedPriceWithTax(true)

			getProratedPriceWithTax({
				subscriptionId: activeSubscriptionId,
				newSubscriptionItem: {
					price_id: applicableKopyBasicPriceId,
					quantity: 1,
				},
			}).then(({ proratedPriceWithTax }) => {
				setLoadingProratedPriceWithTax(false)
				if (proratedPriceWithTax) {
					setProratedPriceWithTax(proratedPriceWithTax)
				}
			})
		}
	}, [])

	useEffect(() => {
		if (state.paddleCustomerId && applicableKopyBasicPriceId) {
			setLoadingPriceWithTax(true)

			getPriceWithTax({
				paddleCustomerId: state.paddleCustomerId,
				priceId: applicableKopyBasicPriceId,
			}).then(({ priceWithTax }) => {
				setLoadingPriceWithTax(false)
				if (priceWithTax) {
					setPriceWithTax(priceWithTax)
				}
			})
		}
	}, [state.paddleCustomerId])

	return (
		<Modal
			title={
				<div
					style={{
						display: "flex",
						gap: "0.3rem",
						alignItems: "center",
						color: "#333",
					}}
				>
					<svg width="1.8rem" height="1.8rem" viewBox="0 0 24 24">
						<path
							fill="currentColor"
							d="M8 20q-.425 0-.712-.288T7 19t.288-.712T8 18h8q.425 0 .713.288T17 19t-.288.713T16 20zm4-4q-.425 0-.712-.288T11 15V7.825L9.1 9.7q-.275.275-.687.288T7.7 9.7q-.275-.275-.275-.7t.275-.7l3.6-3.6q.15-.15.325-.212T12 4.425t.375.063t.325.212l3.6 3.6q.275.275.288.688T16.3 9.7q-.275.275-.7.275t-.7-.275L13 7.825V15q0 .425-.287.713T12 16"
						></path>
					</svg>
					<span style={{ fontSize: "1.4rem", fontWeight: "700" }}>
						{t("upgrade_basic.upgrade_to_kopy_basic")}
					</span>
				</div>
			}
			opened={open}
			onClose={() => setOpen(false)}
			centered
			radius="lg"
			size="30rem"
			classNames={{
				content: "border shadow",
			}}
		>
			<div
				style={{
					marginTop: "2rem",
					marginBottom: "2rem",
					fontSize: "1.2rem",
					color: "#333",
					fontWeight: "700",
				}}
			>
				{t("upgrade_basic.do_you_wish_to_upgrade_to_kopy_basic")}
			</div>

			{state.hasTrialedKopyBasicBefore === false &&
			billingInterval === "monthly" ? (
				<div
					style={{
						background: "#12a112",
						marginLeft: "auto",
						marginRight: "0.5rem",
						marginBottom: "0.2rem",
						width: "fit-content",
						padding: "0.3rem 0.4rem",
						borderRadius: "1rem",
					}}
				>
					<div
						style={{
							fontSize: "0.67rem",
							color: "#fff",
							lineHeight: "1",
						}}
					>
						{t("protect.includes_3_day_free_trial")}
					</div>
				</div>
			) : (
				""
			)}

			<div
				style={{
					background: "#F0F0F0",
					padding: "0.5rem",
					borderRadius: "1rem",
					marginBottom: "3rem",
				}}
			>
				<div
					style={{
						display: "flex",
						gap: "0.7rem",
						alignItems: "center",
						justifyContent: "start",
					}}
				>
					<img
						className="border shadow"
						src={KopyLogo}
						style={{
							width: "2rem",
							height: "2rem",
							objectFit: "cover",
							borderRadius: "0.5rem",
						}}
					/>

					<div>
						<p
							style={{
								fontSize: "0.85rem",
								fontWeight: "700",
								color: "#333",
							}}
						>
							Kopy Basic
						</p>
						<p
							style={{
								fontSize: "0.8rem",
								fontWeight: "500",
								color: "#777",
							}}
						>
							{t("upgrade_basic.copy_products_from_any_store")}
						</p>
					</div>

					<div
						style={{
							marginLeft: "auto",
							display: "flex",
							flexDirection: "column",
							gap: "0.1rem",
							alignItems: "end",
						}}
					>
						{loadingPriceWithTax ? (
							<Spinner />
						) : (
							<div>
								<p
									style={{
										fontSize: "0.85rem",
										fontWeight: "700",
										lineHeight: "1",
										color: "#777",
									}}
								>
									{new Intl.NumberFormat("en-US", {
										style: "currency",
										currency: "USD",
									}).format(
										priceWithTax ||
											applicableKopyBasicPriceInUsd,
									)}{" "}
									{billingInterval === "monthly"
										? t("account.month")
										: billingInterval === "yearly"
											? t("account.year")
											: billingInterval === "quarterly"
												? t("account.quarterly")
												: null}
								</p>
								{!priceWithTax ||
								applicableKopyBasicPriceInUsd >=
									priceWithTax ? (
									<p
										style={{
											fontSize: "0.7rem",
											fontWeight: "400",
											lineHeight: "1",
											color: "#888",
											textAlign: "end",
										}}
									>
										{t("protect.plus_tax")}
									</p>
								) : (
									""
								)}
							</div>
						)}
					</div>
				</div>
			</div>

			{error ? (
				<div
					style={{
						background: "#ff2f2f",
						padding: "1rem",
						borderRadius: "0.5rem",
						marginBottom: "1rem",
					}}
				>
					<p
						style={{
							fontSize: "0.9rem",
							fontWeight: "600",
							color: "#fff",
							marginBottom: "1rem",
						}}
					>
						{error}
					</p>
					<p
						style={{
							fontSize: "0.85rem",
							fontWeight: "500",
							color: "#fff",
							marginBottom: "1rem",
						}}
					>
						If upgrading keeps failing, please check that you have a
						valid payment method.
					</p>
					{activeSubscription?.updatePaymentMethodUrl && (
						<Button
							fullWidth
							onClick={() =>
								window.open(
									activeSubscription?.updatePaymentMethodUrl,
									"_blank",
								)
							}
						>
							Update Payment Method
						</Button>
					)}
				</div>
			) : (
				""
			)}

			<CoolButton
				onClick={loading || showSuccess ? null : _updateSubscription}
			>
				<span style={{ fontWeight: "700" }}>
					{loading ? (
						<Spinner />
					) : showSuccess ? (
						<svg width="1rem" height="1rem" viewBox="0 0 512 512">
							<path
								d="M256 32C132.3 32 32 132.3 32 256s100.3 224 224 224 224-100.3 224-224S379.7 32 256 32zm114.9 149.1L231.8 359.6c-1.1 1.1-2.9 3.5-5.1 3.5-2.3 0-3.8-1.6-5.1-2.9-1.3-1.3-78.9-75.9-78.9-75.9l-1.5-1.5c-.6-.9-1.1-2-1.1-3.2 0-1.2.5-2.3 1.1-3.2.4-.4.7-.7 1.1-1.2 7.7-8.1 23.3-24.5 24.3-25.5 1.3-1.3 2.4-3 4.8-3 2.5 0 4.1 2.1 5.3 3.3 1.2 1.2 45 43.3 45 43.3l111.3-143c1-.8 2.2-1.4 3.5-1.4 1.3 0 2.5.5 3.5 1.3l30.6 24.1c.8 1 1.3 2.2 1.3 3.5.1 1.3-.4 2.4-1 3.3z"
								fill="#1ad313"
							></path>
						</svg>
					) : (
						<>
							{state.hasTrialedKopyBasicBefore
								? t("protect.upgrade.upgrade_now")
								: t("protect.upgrade.start_3_day_free_trial")}
							{proratedPriceWithTax ? (
								<Trans
									i18nKey="upgrade_basic.until_next_renewal"
									values={{
										price: new Intl.NumberFormat("en-US", {
											style: "currency",
											currency: "USD",
										}).format(proratedPriceWithTax),
									}}
								></Trans>
							) : (
								""
							)}
						</>
					)}
				</span>
			</CoolButton>

			{showSuccess && loading ? (
				<p
					style={{
						marginTop: "0.5rem",
						fontSize: "0.8rem",
						color: "#888",
						width: "fit-content",
						marginLeft: "auto",
						marginRight: "auto",
					}}
				>
					{t("protect.upgrade_success_redirecting")}
				</p>
			) : (
				""
			)}
		</Modal>
	)
}
